import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import {
    APIS,
    SF_CO_CD_SRCH,
    SG_CO_CD_EXCEL_LIST,
    SG_CO_CD_LIST,
    URLS,
} from "@/tra/tancis/ptli/usr/cocd/services/PtliCoCdService";
import dayjs from "dayjs";

/*
 * Company Code List
 * !== 업체 코드 목록 ==!
 */
export const PTLI0407001Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const [load, setLoad] = useState(false);

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "CGM0095" },
            { area: "comnCd", comnCd: "CGM0096" },
            { area: "cstmCd" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Company Approval Request List Search !== 업체 사용자 목록 검색 ==!
        coCdSrch: useForm({
            defaultSchema: SF_CO_CD_SRCH,
            defaultValues: { ...pgeStore?.form },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Company Approval Request List !== 업체 사용자 목록 ==!
        coCdList: useGrid({
            defaultSchema: SG_CO_CD_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        coCdExcelList: useGrid({
            defaultSchema: SG_CO_CD_EXCEL_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        //Check Company Code List !==  업체 코드 목록 조회==!
        getCoCdList: useFetch({
            api: (page = grid.coCdList.page) => APIS.getCoCdList(form.coCdSrch.getValues(), page, grid.coCdList.size),
            enabled: comnUtils.isEmpty(form.coCdSrch.errors),
            key: [grid.coCdList.page, grid.coCdList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.coCdSrch.getValues(),
                    page: grid.coCdList.page,
                    size: grid.coCdList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),

        //Check Company Code List !==  업체 코드 엑셀 목록 조회==!
        getCoCdExcelList: useFetch({
            api: (data: any) => (load ? APIS.getCoCdExcelList(data) : null),
            enabled: comnUtils.isEmpty(form.coCdSrch.errors),
            onSuccess: () => {
                if (load) {
                    grid.coCdExcelList.exportExcel({
                        // Internal Portal Customs User Information !== 내부 포털_세관_사용자 정보 ==!
                        excelName: `${t("L_INT_PTL")}_${t("L_CO_CD")}_${t("T_USR_INFO")}_${dayjs().format(
                            "DD/MM/YYYY HHmm",
                        )}`,
                        sheetName: "Sheet1",
                    });
                }
                setLoad(false);
            },
            onError: () => {},
            showToast: true,
        }),
    };
    const render = {
        cell: {
            coCd: (props: any) => {
                const { rowValues, value } = props;
                return <Link to={`/ptli/usr/cocd/ptli0407003s/${rowValues.coTin}/${rowValues.coSn}`}>{value}</Link>;
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        onSrchBtnClick: (page: number = 0) => {
            grid.coCdList.setPage(page);
            fetch.getCoCdList.fetch(page);
        },
        // on New Button Click handler
        onNewBtnClick: () => navigate(`${URLS.ptli0407002s}`),
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchBtnClick(pgeStore?.page);
    }, []);

    useEffect(() => {
        const store = getStore(pgeUid);
        if (!comnUtils.isEmpty(store) || load) {
            fetch.getCoCdExcelList.fetch(store.form);
        }
    }, [load]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.coCdSrch.schema.coTin} />
                            <Group.Control {...form.coCdSrch.schema.coTpCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.coCdSrch.schema.coNm} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.coCdSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="excelDown"
                                onClick={() => {
                                    setLoad(true);
                                }}
                            ></Button>
                        </Layout.Left>
                    </Layout>
                    <Group.Section>
                        <Grid {...grid.coCdList.grid} data={fetch.getCoCdList.data?.coCdList} render={render} />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button role={"new"} onClick={handler.onNewBtnClick} />
                </Layout.Left>
            </Layout>
            <Group hidden>
                <Grid {...grid.coCdExcelList.grid} data={fetch.getCoCdExcelList.data?.coCdExcelList} />
            </Group>
        </Page>
    );
};
